import { wakeLoopDaemonControlPlaneRequestSchema, wakeLoopDaemonControlPlaneResponseSchema, } from "@wakeloop/schemas";
export function parseControlPlaneRequest(input) {
    const parsed = wakeLoopDaemonControlPlaneRequestSchema.safeParse(input);
    if (!parsed.success) {
        throw new Error("invalid daemon control-plane request");
    }
    return parsed.data;
}
export async function buildControlPlaneResponse(input) {
    const request = parseControlPlaneRequest(input.request);
    const requestMeta = {
        ...(request.requestId ? { requestId: request.requestId } : {}),
        v: request.v,
    };
    switch (request.command) {
        case "status":
            return buildStatusControlPlaneResponse({
                payload: input.context.statusPayload,
                requestMeta,
            });
        case "health":
            return buildHealthControlPlaneResponse({
                payload: input.context.healthPayload,
                requestMeta,
            });
        case "history":
            return buildHistoryControlPlaneResponse({
                payload: {
                    entries: input.context.historyEntries,
                },
                requestMeta,
            });
        case "interrupt":
            return await buildInterruptControlPlaneResponse({
                context: input.context,
                payload: request.payload,
                requestMeta,
            });
        case "cancel":
            return await buildCancelControlPlaneResponse({
                context: input.context,
                payload: request.payload,
                requestMeta,
            });
        case "conversation_execution_status":
            return await buildConversationExecutionStatusControlPlaneResponse({
                context: input.context,
                payload: request.payload,
                requestMeta,
            });
        case "sync_route_catalog":
            return await buildSyncRouteCatalogControlPlaneResponse({
                context: input.context,
                payload: request.payload,
                requestMeta,
            });
        case "set_local_agent_enabled":
            return await buildSetLocalAgentEnabledControlPlaneResponse({
                context: input.context,
                payload: request.payload,
                requestMeta,
            });
        case "shutdown":
            return await buildShutdownControlPlaneResponse({
                context: input.context,
                payload: request.payload,
                requestMeta,
            });
        default:
            throw new Error("unsupported daemon control-plane command");
    }
}
function buildStatusControlPlaneResponse(input) {
    return parseControlPlaneResponse({
        ...input.requestMeta,
        code: "service.status",
        payload: input.payload,
    });
}
function buildHealthControlPlaneResponse(input) {
    return parseControlPlaneResponse({
        ...input.requestMeta,
        code: "service.health",
        payload: input.payload,
    });
}
function buildHistoryControlPlaneResponse(input) {
    return parseControlPlaneResponse({
        ...input.requestMeta,
        code: "service.history",
        payload: input.payload,
    });
}
async function buildInterruptControlPlaneResponse(input) {
    const payload = input.context.interruptHandler
        ? await input.context.interruptHandler(input.payload)
        : {
            accepted: false,
            note: "interrupt_not_implemented",
            target: input.payload.target,
        };
    return parseControlPlaneResponse({
        ...input.requestMeta,
        code: "service.interrupt",
        payload,
    });
}
async function buildCancelControlPlaneResponse(input) {
    const payload = input.context.cancelHandler
        ? await input.context.cancelHandler(input.payload)
        : {
            accepted: false,
            note: "cancel_not_implemented",
            target: input.payload.target,
        };
    return parseControlPlaneResponse({
        ...input.requestMeta,
        code: "service.cancel",
        payload,
    });
}
async function buildConversationExecutionStatusControlPlaneResponse(input) {
    const payload = input.context.conversationExecutionStatusHandler
        ? await input.context.conversationExecutionStatusHandler(input.payload)
        : {
            agentContextLookupKey: [
                input.payload.spaceId,
                input.payload.targetProfileId,
                encodeURIComponent(input.payload.agentControllerRef),
                input.payload.transportMode,
                "runtime-workspace-unresolved",
            ].join("|"),
            controllerLaunchStatus: "missing",
            executionState: null,
            localRuntimeContext: {
                agentContextLookupKey: [
                    input.payload.spaceId,
                    input.payload.targetProfileId,
                    encodeURIComponent(input.payload.agentControllerRef),
                    input.payload.transportMode,
                    "runtime-workspace-unresolved",
                ].join("|"),
                runtimeWorkspaceFingerprint: null,
                status: "missing",
            },
            providerConversationProof: {
                agentControllerConversationId: input.payload.agentControllerConversationId,
                checkedAt: null,
                errorCode: null,
                errorMessage: null,
                errorType: null,
                expiresAt: null,
                intrusive: false,
                method: null,
                provider: null,
                status: "not_run",
                transportMode: input.payload.transportMode,
            },
            runtimeWorkspace: {
                configPath: ".",
                configuredWorkspacePath: null,
                fingerprint: null,
                path: null,
                reasonCode: "workspace.missing",
                reasonText: "Conversation execution status handler is not available on this device.",
                runtimeContext: null,
                status: "missing",
                workspaceMode: null,
            },
        };
    return parseControlPlaneResponse({
        ...input.requestMeta,
        code: "service.conversation_execution_status",
        payload,
    });
}
async function buildShutdownControlPlaneResponse(input) {
    const resolvedShutdownPayload = input.payload ?? {
        mode: "graceful",
        gracePeriodMs: 5000,
    };
    const payload = input.context.shutdownHandler
        ? await input.context.shutdownHandler(resolvedShutdownPayload)
        : {
            accepted: false,
            mode: resolvedShutdownPayload.mode,
            gracePeriodMs: resolvedShutdownPayload.gracePeriodMs,
        };
    return parseControlPlaneResponse({
        ...input.requestMeta,
        code: "service.shutdown",
        payload,
    });
}
async function buildSyncRouteCatalogControlPlaneResponse(input) {
    const payload = input.context
        .syncRouteCatalogHandler
        ? await input.context.syncRouteCatalogHandler(input.payload)
        : {
            accepted: false,
            note: "sync_route_catalog_not_implemented",
            profileIds: [],
            syncStatus: "deferred",
        };
    return parseControlPlaneResponse({
        ...input.requestMeta,
        code: "service.sync_route_catalog",
        payload,
    });
}
async function buildSetLocalAgentEnabledControlPlaneResponse(input) {
    const payload = input
        .context.setLocalAgentEnabledHandler
        ? await input.context.setLocalAgentEnabledHandler(input.payload)
        : {
            accepted: false,
            agentId: input.payload.agentId,
            changed: false,
            enabled: input.payload.enabled,
            message: "Local agent enablement control is not available on this WakeLoop Service.",
            reasonCode: "set_local_agent_enabled_not_implemented",
            reportSyncStatus: "failed",
            status: "rejected",
        };
    return parseControlPlaneResponse({
        ...input.requestMeta,
        code: "service.set_local_agent_enabled",
        payload,
    });
}
function parseControlPlaneResponse(input) {
    const parsed = wakeLoopDaemonControlPlaneResponseSchema.safeParse(input);
    if (!parsed.success) {
        throw new Error("invalid daemon control-plane response");
    }
    return parsed.data;
}
